/**
 * Billing Configuration Modal Component
 * 
 * Allows admins to setup default values for billing configuration including:
 * - Sign Up Credits (default_signup_credits)
 * - Monthly Limit (default_monthly_allowance)
 */

import React, { useState, useEffect } from 'react';
import { X, Settings, DollarSign, Calendar, CalendarDays } from 'lucide-react';
import { adminApi, handleAdminApiError } from '../services/adminApi';
import type { BillingConfig } from '../types';

interface BillingConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface BillingConfigForm {
  default_signup_credits: number;
  default_monthly_allowance: number;
}

export const BillingConfigModal: React.FC<BillingConfigModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<BillingConfigForm>({
    default_signup_credits: 50,
    default_monthly_allowance: 50
  });

  // Load current configuration when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCurrentConfig();
    }
  }, [isOpen]);

  const loadCurrentConfig = async () => {
    setLoadingConfig(true);
    setError(null);
    
    try {
      const response = await adminApi.getBillingConfig();
      
      setFormData({
        default_signup_credits: response.config.default_signup_credits || 50,
        default_monthly_allowance: response.config.default_monthly_allowance || 50
      });
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(`Failed to load current configuration: ${errorMessage}`);
      console.error('Failed to load billing config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleInputChange = (field: keyof BillingConfigForm, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      [field]: numericValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate inputs
      if (formData.default_signup_credits < 0) {
        throw new Error('Sign up credits cannot be negative');
      }
      if (formData.default_monthly_allowance < 0) {
        throw new Error('Monthly limit cannot be negative');
      }

      // Update billing configuration
      await adminApi.updateBillingConfig(formData);
      
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to update billing config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Settings className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Billing Configuration
              </h3>
              <p className="text-sm text-gray-500">
                Setup default values for new users
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Loading State */}
          {loadingConfig && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading current configuration...</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form Fields */}
          {!loadingConfig && (
            <div className="space-y-4">
              {/* Sign Up Credits */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                  Sign Up Credits
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.default_signup_credits}
                  onChange={(e) => handleInputChange('default_signup_credits', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="50.00"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Free credits given to new users upon signup
                </p>
              </div>

              {/* Monthly Limit */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <CalendarDays className="h-4 w-4 mr-2 text-purple-600" />
                  Monthly Limit
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.default_monthly_allowance}
                  onChange={(e) => handleInputChange('default_monthly_allowance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="50.00"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default monthly allowance for users with monthly plans
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingConfig}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {loading ? 'Updating...' : 'Update Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingConfigModal;
